/**
 * Chooses which speed labels can be drawn without overlapping.
 *
 * The map used to drop every label past a fixed zoom, which meant the offshore
 * buoys — sitting alone in open water, colliding with nothing — lost their
 * numbers because five stations crowd the central Bay. Zoomed out is exactly
 * when an offshore number is most worth reading, so the decision has to be per
 * label rather than global.
 *
 * Kept as a pure function over boxes so the placement rule can be tested without
 * a map, a browser, or a layout pass.
 */

export interface LabelBox {
  id: string;
  /** Higher wins a contested spot. */
  rank: number;
  left: number;
  right: number;
  top: number;
  bottom: number;
}

function overlaps(a: LabelBox, b: LabelBox, gap: number): boolean {
  return (
    a.left - gap < b.right &&
    b.left - gap < a.right &&
    a.top - gap < b.bottom &&
    b.top - gap < a.bottom
  );
}

/**
 * Greedy best-first placement: walk the candidates in rank order and keep each
 * one whose box clears everything kept so far.
 *
 * Greedy rather than optimal on purpose. Maximising the number of labels shown
 * is a packing problem, and the optimum would reshuffle which stations are
 * labelled on tiny camera moves — labels flickering between neighbours as you
 * pan is worse than showing one fewer. Rank order makes the outcome stable and
 * predictable: the same stations win the same contests every frame.
 *
 * Ties break on `id` so the result never depends on input order.
 */
export function pickVisibleLabels(boxes: readonly LabelBox[], gap = 0): Set<string> {
  const ordered = [...boxes].sort((a, b) => b.rank - a.rank || a.id.localeCompare(b.id));
  const kept: LabelBox[] = [];
  const visible = new Set<string>();

  for (const box of ordered) {
    if (kept.some((k) => overlaps(box, k, gap))) continue;
    kept.push(box);
    visible.add(box.id);
  }

  return visible;
}
